// Ranking da liga (canvas E01 e 6.4b): Cidade, Estado e Brasil; Semana, Mês e Temporada. Abre na cidade do jogador;
// sem cidade, no Brasil. Top 100, com a sua posição fixa no rodapé, mesmo fora do top. Cidade e Estado mostram só quem
// está com "Aparecer no ranking da cidade" ligado; sem cidade, mostram o convite para escolher a cidade e a sua
// posição no Brasil.
// Rei do Pote (canvas 6.4c): a terceira aba, com o saldo de mesa nas salas valendo Tentos (o que ganhou menos o que
// perdeu). Tentos do plano e da loja ficam de fora; a tela não tem botão de compra.
import { SeloNoTopo } from '../comum/SeloGratis.tsx';
import { type ReactNode, useEffect, useState } from 'react';
import type { EscopoRanking, LinhaRanking, Periodo, RankingNaTela, TipoRanking } from '@truco6/gamificacao';
import { lerRanking } from '../comum/api.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { ir } from '../comum/rotas.ts';
import { Coroa, Rosto } from '../comum/Rosto.tsx';
import { BotaoAmizade, IconeAmigos } from '../comum/BotaoAmizade.tsx';
import { abrirPerfil } from '../comum/perfilAberto.ts';
import { saldoDeMesa } from '../comum/pote.ts';
import { FolhaDePessoas } from '../comum/FolhaDePessoas.tsx';
import { Tela, Topo } from '../telas/comum.tsx';
import { Tento } from '../telas/tentos-comum.tsx';
import { Escudo } from './trios.tsx';
import { AbasDasLigas } from './comunidades.tsx';

const ESCOPOS: [EscopoRanking, string][] = [['cidade', 'Cidade'], ['estado', 'Estado'], ['brasil', 'Brasil']];
const PERIODOS: [Periodo, string][] = [['semana', 'Semana'], ['mes', 'Mês'], ['temporada', 'Temporada']];
const TIPOS: [TipoRanking, ReactNode][] = [['jogadores', 'Jogadores'], ['trios', 'Trios'], ['pote', <><Tento tamanho={14} vinco={false} />Rei do Pote</>]];
const NA: Record<EscopoRanking, string> = { cidade: 'na cidade', estado: 'no estado', brasil: 'no Brasil' };
const pts = (n: number) => n.toLocaleString('pt-BR') + ' pts';
const partidasEV = (l: LinhaRanking) => `${l.partidas} ${l.partidas === 1 ? 'partida' : 'partidas'} · ${l.vitorias ?? 0} V`;

function Segmentos<T extends string>({ rotulo, valor, opcoes, aoMudar, classe }: {
  rotulo: string; valor: T; opcoes: [T, ReactNode][]; aoMudar: (v: T) => void; classe: string;
}) {
  return (
    <div className={classe} role="radiogroup" aria-label={rotulo}>
      {opcoes.map(([v, nome]) => (
        <button key={v} role="radio" aria-checked={valor === v} className={valor === v ? 'on' : undefined} onClick={() => aoMudar(v)}>{nome}</button>
      ))}
    </div>
  );
}

export function RankingTela({ sessao, perfil, tipoInicial = 'jogadores' }: { sessao: Sessao; perfil: PerfilConta; tipoInicial?: TipoRanking }) {
  const [escopo, setEscopo] = useState<EscopoRanking>(perfil.cidadeIbge ? 'cidade' : 'brasil');
  const [periodo, setPeriodo] = useState<Periodo>('semana');
  const [tipo, setTipo] = useState<TipoRanking>(tipoInicial);
  const [r, setR] = useState<RankingNaTela | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [trio, setTrio] = useState<LinhaRanking | null>(null); // o trio aberto, com os 3 jogadores

  useEffect(() => {
    let vivo = true;
    setErro(null);
    lerRanking(sessao, tipo, escopo, periodo).then((x) => { if (vivo) setR(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id, tipo, escopo, periodo]);

  const atual = r && r.tipo === tipo && r.escopo === escopo && r.periodo === periodo ? r : null;
  const meu = (l: LinhaRanking) => (tipo === 'trios' ? atual?.eu?.id === l.id : l.id === sessao.id);
  const pote = tipo === 'pote';
  const podio = pote ? [] : atual?.linhas.slice(0, 3) ?? [], resto = pote ? atual?.linhas ?? [] : atual?.linhas.slice(3) ?? [];
  const semCidade = escopo !== 'brasil' && atual?.lugar === null;
  // Sem cidade, em Cidade e Estado: o rodapé mostra a posição no Brasil.
  const [noBrasil, setNoBrasil] = useState<RankingNaTela | null>(null);
  useEffect(() => {
    if (!semCidade) return;
    let vivo = true;
    lerRanking(sessao, tipo, 'brasil', periodo).then((x) => { if (vivo) setNoBrasil(x); }, () => {});
    return () => { vivo = false; };
  }, [semCidade, sessao.id, tipo, periodo]);
  const brasil = semCidade && noBrasil?.tipo === tipo && noBrasil.periodo === periodo ? noBrasil : null;

  return (
    <Tela larga>
      {/* Ligas é aba principal: sem o voltar. O título e as abas ficam acima das duas colunas. */}
      <div className="topo-com-acao topo-ranking">
        <Topo titulo="Ranking" />
        <span className="nota-do-ranking">{pote ? 'Salas valendo Tentos' : 'Todas as partidas'}</span>
      </div>
      <SeloNoTopo />
      <AbasDasLigas atual="liga" />
      <div className="ranking">
        <div className="ranking-filtros">
          <Segmentos classe="segmentos-periodo tipos-ranking" rotulo="Ranking de" valor={tipo} opcoes={TIPOS} aoMudar={setTipo} />
          <Segmentos classe="chips-escopo" rotulo="Onde" valor={escopo} opcoes={ESCOPOS} aoMudar={setEscopo} />
          <div className="ranking-lugar">
            <p><IconeLugar /><b>{atual?.lugar ?? (escopo === 'brasil' ? 'Brasil' : 'Sem cidade')}</b></p>
            <Segmentos classe="segmentos-periodo" rotulo="Período" valor={periodo} opcoes={PERIODOS} aoMudar={setPeriodo} />
          </div>
        </div>
        <div className="ranking-lista">
          {podio.length > 0 && (
            <ol className="podio" aria-label="Pódio">
              {[1, 0, 2].map((i) => (podio[i] ? <Degrau key={i} l={podio[i]!} eu={meu(podio[i]!)} abrirTrio={setTrio} /> : <li key={i} className="degrau vazio" aria-hidden="true" />))}
            </ol>
          )}
          {erro && <p className="erro" role="alert">{erro}</p>}
          {!atual && !erro && <p className="carregando">Carregando o ranking...</p>}
          {pote && !semCidade && (
            <p className="faixa-pote"><Coroa tamanho={24} /><span>Quem tem o maior saldo nas salas valendo Tentos: o que ganhou menos o que perdeu. Tentos do plano e da loja ficam de fora.</span></p>
          )}
          {semCidade && <SemCidade aoVerBrasil={() => setEscopo('brasil')} />}
          {atual && !semCidade && escopo !== 'brasil' && !atual.consentiu && (
            <p className="nota aviso-ranking">{tipo === 'trios'
              ? 'Trio aparece aqui pela cidade do capitão, quando ele deixa ligado "Aparecer no ranking da cidade".'
              : 'Você não aparece aqui. Ligue "Aparecer no ranking da cidade" no Perfil ou em Configurações.'}</p>
          )}
          {atual && !semCidade && atual.linhas.length === 0 && (pote
            ? <p className="nota centro">Ninguém tem saldo positivo ainda {periodo === 'semana' ? 'nesta semana' : periodo === 'mes' ? 'neste mês' : 'nesta temporada'}. Ganhe uma sala valendo Tentos e saia na frente.</p>
            : <p className="nota centro">Ninguém pontuou ainda {periodo === 'semana' ? 'nesta semana' : periodo === 'mes' ? 'neste mês' : 'nesta temporada'}. Jogue uma partida e saia na frente.</p>)}
          {resto.length > 0 && (pote
            ? <ol className="lista-ranking lista-pote" aria-label={`Rei do Pote ${periodo === 'semana' ? 'da semana' : periodo === 'mes' ? 'do mês' : 'da temporada'}`}>
                {resto.map((l) => <LinhaPote key={l.id} l={l} eu={meu(l)} />)}
              </ol>
            : <ol className="lista-ranking" start={4}>
                {resto.map((l) => <Linha key={l.id} l={l} eu={meu(l)} abrirTrio={setTrio} />)}
              </ol>)}
        </div>
      </div>
      {trio && <FolhaDePessoas titulo={trio.nome} subtitulo="Os 3 do trio" pessoas={trio.jogadores ?? []} eu={sessao.id} aoFechar={() => setTrio(null)} />}
      {brasil && (pote ? <RodapePote eu={brasil.eu} noBrasil /> : (
        <div className="minha-posicao" role="status">
          <IconeLugar />
          {brasil.eu
            ? <><span>{tipo === 'trios' ? `${brasil.eu.nome} no Brasil` : 'Você no Brasil'}</span><b>{brasil.eu.posicao}º</b><small>{pts(brasil.eu.pontos)}</small></>
            : <span>{tipo === 'trios' ? 'Jogue com o seu trio para entrar no ranking do Brasil.' : 'Jogue uma partida para entrar no ranking do Brasil.'}</span>}
        </div>
      ))}
      {atual && !semCidade && pote && <RodapePote eu={atual.eu} />}
      {atual && !semCidade && !pote && (
        <div className="minha-posicao" role="status">
          <IconeLugar />
          {atual.eu
            ? <><span>{tipo === 'trios' ? `${atual.eu.nome} ${NA[escopo]}:` : `Sua posição ${NA[escopo]}:`}</span><b>{atual.eu.posicao}º</b><small>{pts(atual.eu.pontos)}</small></>
            : <span>{tipo === 'trios' ? 'Jogue com o seu trio para entrar no ranking.' : 'Jogue uma partida para entrar no ranking.'}</span>}
        </div>
      )}
    </Tela>
  );
}

// Rei do Pote (canvas 6.4c): posição, rosto, apelido, partidas e vitórias, o saldo em dourado com o grão e o botão
// de amizade. O 1º tem a borda dourada e a coroa.
function LinhaPote({ l, eu }: { l: LinhaRanking; eu: boolean }) {
  const nome = eu ? 'Você' : l.nome;
  return (
    <li className={'linha-ranking jogador linha-pote' + (l.posicao === 1 ? ' primeiro' : '') + (eu ? ' meu' : '')}>
      <button className="abrir-jogador" aria-label={`${l.posicao}º ${nome}, saldo de ${saldoDeMesa(l.pontos)} Tentos. Ver perfil`} onClick={() => abrirPerfil(l.id)}>
        <span className="posicao">{l.posicao}º</span>
        <Rosto perfil={{ apelido: l.nome, avatar: l.avatar }} usuario={l.id} cor={eu ? 'var(--nos-400)' : l.posicao === 1 ? 'var(--gold-400)' : 'var(--line-600)'}
          tamanho={40} coroa={l.posicao === 1 || !!l.coroa} />
        <span className="quem"><b>{nome}</b><small>{[partidasEV(l), l.local].filter(Boolean).join(' · ')}</small></span>
        <span className="saldo-pote">{saldoDeMesa(l.pontos)}<Tento tamanho={14} vinco={false} /></span>
      </button>
      {!eu && <BotaoAmizade usuario={l.id} apelido={l.nome} compacto />}
    </li>
  );
}

// O rodapé do Rei do Pote: o seu saldo, mesmo negativo, quanto falta para o top 10 e o atalho para as salas valendo
// Tentos. Sem nenhum botão de compra.
function RodapePote({ eu, noBrasil = false }: { eu: LinhaRanking | null; noBrasil?: boolean }) {
  const saldo = eu?.pontos ?? 0, dentro = !!eu && eu.posicao > 0;
  const apoio = !dentro ? 'Ganhe uma sala valendo Tentos para entrar'
    : eu!.faltaTop10 != null ? `Faltam ${eu!.faltaTop10} Tentos de saldo para o top 10` : partidasEV(eu!);
  return (
    <div className="rodape-pote">
      <div className="minha-posicao pote" role="status">
        <Coroa tamanho={22} />
        <span className="eu-pote"><b>{noBrasil ? 'Você no Brasil' : 'Você'} · <em>{saldoDeMesa(saldo)}</em></b><small>{apoio}</small></span>
        {dentro && <b>{eu!.posicao}º</b>}
      </div>
      <button className="btn2 largo ver-salas-tentos" onClick={() => ir({ tela: 'inicio', aba: 'tentos' })}><Tento tamanho={18} vinco={false} />Ver salas valendo Tentos</button>
    </div>
  );
}

// Cidade e Estado sem cidade no perfil (canvas 6.4b): o convite para escolher a cidade e o atalho para o Brasil.
function SemCidade({ aoVerBrasil }: { aoVerBrasil: () => void }) {
  return (
    <section className="cartao sem-cidade" aria-labelledby="sem-cidade-titulo">
      <span className="pino-sem-cidade" aria-hidden="true"><IconeLugar tamanho={36} /></span>
      <h2 id="sem-cidade-titulo">Bota sua cidade no mapa</h2>
      <p>Escolha a cidade e você entra no ranking dela e do seu estado. Até lá, você aparece só no ranking do Brasil.</p>
      <button className="btn1 largo" onClick={() => ir({ tela: 'perfil', campo: 'cidade' })}>ESCOLHER CIDADE</button>
      <button className="link" onClick={aoVerBrasil}>Ver o ranking do Brasil</button>
    </section>
  );
}

function Degrau({ l, eu, abrirTrio }: { l: LinhaRanking; eu: boolean; abrirTrio: (l: LinhaRanking) => void }) {
  return (
    <li className={'degrau d' + l.posicao + (eu ? ' meu' : '')} aria-label={`${l.posicao}º ${eu ? 'Você' : l.nome}, ${pts(l.pontos)}`}>
      {l.escudo ? <Escudo id={l.escudo} tamanho={l.posicao === 1 ? 56 : 46} />
        : <Rosto perfil={{ apelido: l.nome, avatar: l.avatar }} usuario={l.id} cor={eu ? 'var(--nos-400)' : 'var(--gold-400)'} tamanho={l.posicao === 1 ? 64 : 52} coroa={!!l.coroa} />}
      <b className="degrau-nome">{eu && !l.escudo ? 'Você' : l.nome}</b>
      <span className="degrau-pts">{pts(l.pontos)}</span>
      {l.escudo ? <button className="ver-trio" aria-label={`Ver os 3 do trio ${l.nome}`} onClick={() => abrirTrio(l)}><IconeAmigos /></button>
        : !eu && <BotaoAmizade usuario={l.id} apelido={l.nome} compacto />}
      <span className="degrau-base">{l.posicao}º</span>
    </li>
  );
}

function Linha({ l, eu, abrirTrio }: { l: LinhaRanking; eu: boolean; abrirTrio: (l: LinhaRanking) => void }) {
  const conteudo = (
    <>
      <span className="posicao">{l.posicao}º</span>
      {l.escudo ? <Escudo id={l.escudo} tamanho={36} />
        : <Rosto perfil={{ apelido: l.nome, avatar: l.avatar }} usuario={l.id} cor={eu ? 'var(--nos-400)' : 'var(--line-600)'} tamanho={40} coroa={!!l.coroa} />}
      <span className="quem"><b>{eu && !l.escudo ? 'Você' : l.nome}</b>
        <small>{[l.membros?.join(', '), l.local, `${l.partidas} ${l.partidas === 1 ? 'partida' : 'partidas'}`].filter(Boolean).join(' · ')}</small></span>
      <span className="pontos">{l.pontos.toLocaleString('pt-BR')}<small>pts</small></span>
    </>
  );
  // Trio: a linha toda abre os 3 jogadores, cada um com o botão de amizade. Jogador: o botão fica no fim da linha.
  if (l.escudo) {
    return <li className={'linha-ranking trio' + (eu ? ' meu' : '')}><button className="abrir-trio" aria-label={`${l.posicao}º ${l.nome}. Ver os 3 do trio`} onClick={() => abrirTrio(l)}>{conteudo}</button></li>;
  }
  // Jogador: rosto e nome abrem o perfil; o botão de amizade fica no fim da linha.
  return (
    <li className={'linha-ranking jogador' + (eu ? ' meu' : '')}>
      <button className="abrir-jogador" aria-label={`${l.posicao}º ${eu ? 'Você' : l.nome}, ${l.pontos} pontos. Ver perfil`} onClick={() => abrirPerfil(l.id)}>{conteudo}</button>
      {!eu && <BotaoAmizade usuario={l.id} apelido={l.nome} compacto />}
    </li>
  );
}

function IconeLugar({ tamanho = 18 }: { tamanho?: number }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}
